import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import api from '../services/api';
import { REACT_APP_GOOGLE_CLIENT_ID, AUTH_GOOGLE_REGISTER, ACCEPT_INVITE } from '../config/Config';
import {
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import StudentSelfConsentSection from '../components/legal/StudentSelfConsentSection';

const persistInviteAuth = (data, fallbackEmail) => {
  if (!data?.token || !data?.user) {
    return false;
  }

  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');

  sessionStorage.setItem('token', data.token);
  sessionStorage.setItem('user', JSON.stringify(data.user));
  localStorage.setItem('lastLoginEmail', data.user?.email || data.email || fallbackEmail || '');
  return true;
};

export const GoogleInviteRegisterButton = ({ selectedRole, inviteToken, onError, studentSelfConsent, agreeToTerms, legalDocumentAvailable }) => {
  const navigate = useNavigate();

  const createNavigationState = (data, message, email) => ({
    message,
    email: data.user?.email || data.email || email || 'user@example.com',
    isInviteBased: data.is_invite_based || false,
    inviteTeacher: data.invite_teacher,
    inviteCount: data.invite_count,
    inviteToken: data.invite_token,
  });

  const handleCredentialResponse = useCallback(async (response) => {
    const id_token = response.credential;

    if (!inviteToken) {
      alert('No invitation token found. Please use a valid invitation link.');
      return;
    }

    try {
      if (studentSelfConsent && !agreeToTerms) {
        alert('Please accept the Terms of Service and Privacy Policy before continuing.');
        return;
      }
      const requestBody = {
        id_token,
        role: selectedRole.toUpperCase(),
        plan_code: 'student_starter',
        invite_token: inviteToken,
        agreeToTerms: studentSelfConsent ? !!agreeToTerms : true,
        student_self_consent: !!studentSelfConsent,
      };

      const res = await api.post(AUTH_GOOGLE_REGISTER, requestBody);
      const data = res.data;

      if (res.status === 200 || res.status === 201) {
        if (data && data.action) {
          const { action, message } = data;
          onError(message);

          switch (action) {
            case 'redirect_to_dashboard':
              persistInviteAuth(data, data.user?.email || data.email);
              navigate('/dashboard', {
                replace: true,
                state: createNavigationState(data, message),
              });
              break;
            case 'redirect_to_accept_invite': {
              const responseInviteToken = data.invite_token;
              if (responseInviteToken) {
                navigate(`/accept-invite?token=${responseInviteToken}`, {
                  replace: true,
                  state: createNavigationState(data, message),
                });
              } else {
                navigate('/login', {
                  replace: true,
                  state: { message, email: data.user?.email || data.email || 'user@example.com' },
                });
              }
              break;
            }
            case 'redirect_to_login':
            default:
              navigate('/login', {
                replace: true,
                state: { message, email: data.user?.email || data.email || 'user@example.com' },
              });
              break;
          }
          return;
        }

        if (data && data.message) {
          onError(data.message);
          navigate('/login', {
            replace: true,
            state: { message: data.message, email: data.user?.email || data.email || 'user@example.com' },
          });
        } else {
          onError('Registration successful! Please check your email for verification.');
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1000);
        }
      } else {
        alert(data.error || 'Registration failed.');
        if (window.google?.accounts?.id?.disableAutoSelect) {
          window.google.accounts.id.disableAutoSelect();
        }
      }
    } catch (err) {
      console.error('Google registration error:', err);

      if (err.response?.status === 400) {
        alert(`Registration failed: ${err.response.data.error || 'Bad request'}`);
      } else if (err.response?.status === 500) {
        alert('Server error during registration. Please try again.');
      } else {
        alert('Not Registered yet. Redirecting to registration');
        navigate('/get-started');
      }
    }
  }, [agreeToTerms, inviteToken, navigate, onError, selectedRole, studentSelfConsent]);

  useEffect(() => {
    const interval = setInterval(() => {
      const google = window.google;
      const buttonEl = document.getElementById('google-invite-register-btn');
      if (google && buttonEl) {
        google.accounts.id.initialize({
          client_id: REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
        });
        google.accounts.id.renderButton(buttonEl, {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'signup_with',
          context: 'signup',
        });
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [handleCredentialResponse, inviteToken, selectedRole]);

  if (studentSelfConsent && legalDocumentAvailable !== true) {
    return (
      <Button
        variant="outlined"
        fullWidth
        disabled
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          fontWeight: 600,
          py: 1.2,
          px: 3,
        }}
      >
        Sign up with Google
      </Button>
    );
  }

  return <div id="google-invite-register-btn"></div>;
};

const InviteRegister = ({ onError }) => {
  const [selectedRole, setSelectedRole] = useState('STUDENT');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    role: 'STUDENT',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [studentSelfConsent, setStudentSelfConsent] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [legalDocumentAvailable, setLegalDocumentAvailable] = useState(null);
  const navigate = useNavigate();

  const inviteToken = searchParams.get('token') || location.state?.inviteToken;
  const inviteEmail = searchParams.get('email') || location.state?.inviteEmail;
  const teacherName = location.state?.teacherName || '';
  const isGmailInvite = ((inviteEmail || '').toLowerCase().endsWith('@gmail.com'));

  useEffect(() => {
    if (!inviteToken) {
      navigate('/login', {
        replace: true,
        state: { message: 'Invalid invitation link. Please contact your teacher for a new invitation.' },
      });
      return;
    }

    if (inviteEmail) {
      setFormData((prev) => ({ ...prev, email: inviteEmail }));
    }

    setSelectedRole('STUDENT');
    setFormData((prev) => ({ ...prev, role: 'STUDENT' }));
  }, [inviteToken, inviteEmail, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const registrationData = {
        email: formData.email,
        role: selectedRole.toUpperCase(),
        plan_code: 'student_starter',
        invite_token: inviteToken,
        agreeToTerms: !!agreeToTerms,
        student_self_consent: !!studentSelfConsent,
      };

      const response = await authAPI.register(registrationData);
      const responseData = response.data || response;

      if (responseData && responseData.action) {
        const { action, message } = responseData;
        setSuccess(message);
        onError(message);

        switch (action) {
          case 'redirect_to_dashboard':
            persistInviteAuth(responseData, formData.email);
            navigate('/dashboard', {
              replace: true,
              state: {
                message,
                email: responseData.email || formData.email,
                isInviteBased: responseData.is_invite_based || false,
                inviteTeacher: responseData.invite_teacher,
                inviteCount: responseData.invite_count,
              },
            });
            break;
          case 'redirect_to_accept_invite':
            navigate(`/accept-invite?token=${responseData.invite_token || inviteToken}`, {
              replace: true,
              state: {
                message,
                inviteToken: responseData.invite_token || inviteToken,
                isInviteBased: true,
              },
            });
            break;
          case 'redirect_to_login':
          default:
            navigate('/login', {
              replace: true,
              state: {
                message,
                email: responseData.email || formData.email,
                needsEmailVerification: responseData.needsEmailVerification || false,
              },
            });
            break;
        }
        return;
      }

      if (responseData && responseData.message) {
        setSuccess(responseData.message);
        onError(responseData.message);
        navigate('/login', {
          replace: true,
          state: {
            message: responseData.message,
            email: responseData.email || formData.email,
          },
        });
      } else {
        setSuccess('Registration successful! Please check your email for verification.');
        onError('Registration successful! Please check your email for verification.');
        navigate('/login', { replace: true });
      }
    } catch (submitError) {
      console.error('Registration error:', submitError);
      const errorMessage = submitError.response?.data?.error || submitError.message || 'Registration failed';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    if (!inviteToken) {
      navigate('/', { replace: true });
      return;
    }
    setRejectLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.post(`${ACCEPT_INVITE}?token=${inviteToken}`, { action: 'reject' });
      const message = response.data?.message || 'Invitation rejected.';
      setSuccess(message);
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 600);
    } catch (rejectError) {
      const message = rejectError.response?.data?.detail || rejectError.response?.data?.error || 'Failed to reject invitation.';
      setError(message);
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #667eea 50%, #764ba2 75%, #f093fb 100%)', p: 2 }}>
        <Box sx={{ width: '100%', maxWidth: 440, background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(20px)', borderRadius: 3, boxShadow: '0 8px 32px rgba(255, 255, 255, 0.15)', p: { xs: 3, sm: 4 }, border: '1px solid rgba(255, 255, 255, 0.3)', position: 'relative', overflow: 'hidden', '&:before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: 'linear-gradient(90deg, #FF6A3D 0%, #FF9E4F 50%, #FFD15C 100%)' } }}>
          <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, background: 'linear-gradient(90deg, #FF6A3D 0%, #FF9E4F 50%, #FFD15C 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1 }}>
              {teacherName ? `Accept invitation from your teacher ${teacherName}` : 'Accept invitation from your teacher'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete your registration to join your teacher&apos;s class. Name and password will be collected later during profile completion.
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                I am a...
              </Typography>
              <Box sx={{ background: 'linear-gradient(135deg, #FF6A3D 0%, #FF9E4F 50%, #FFD15C 100%)', color: '#fff', px: 3, py: 1, borderRadius: '16px', display: 'inline-block', fontWeight: 600, border: '1px solid rgba(255, 106, 61, 0.3)', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                Student
              </Box>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ background: 'linear-gradient(135deg, #FF6A3D 0%, #FF9E4F 50%, #FFD15C 100%)', color: '#fff', px: 2, py: 0.5, borderRadius: '16px', display: 'inline-block', fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                Plan: Starter (Free) - Teacher Invite
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="off"
              disabled={Boolean(inviteEmail)}
              InputProps={inviteEmail ? { readOnly: true } : {}}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  backgroundColor: 'rgba(255, 255, 255, 0.96)',
                },
              }}
            />

            <StudentSelfConsentSection
              studentSelfConsent={studentSelfConsent}
              agreeToTerms={agreeToTerms}
              onStudentSelfConsentChange={(checked) => {
                setStudentSelfConsent(checked);
                if (!checked) {
                  setAgreeToTerms(false);
                }
              }}
              onAgreeToTermsChange={setAgreeToTerms}
              onLegalAvailabilityChange={setLegalDocumentAvailable}
              consentTestId="invite-register-student-self-consent"
              termsTestId="invite-register-terms-checkbox"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={isLoading || rejectLoading || (studentSelfConsent && legalDocumentAvailable !== true)}
              sx={{
                mt: 2,
                mb: 2,
                background: 'linear-gradient(to right, #FF6A3D, #FF9E4F, #FFD15C)',
                boxShadow: '0 8px 25px rgba(255, 106, 61, 0.4)',
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(255, 106, 61, 0.5)',
                  background: 'linear-gradient(to right, #FF5A2D, #FF8E3F, #FFC14C)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  transform: 'none',
                  boxShadow: 'none',
                },
              }}
            >
              {isLoading ? <><CircularProgress size={20} /> Creating Account...</> : 'Accept'}
            </Button>

            <Button
              type="button"
              fullWidth
              variant="outlined"
              size="large"
              disabled={isLoading || rejectLoading}
              onClick={handleReject}
              sx={{
                mb: 2,
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: 600,
                py: 1.35,
                borderColor: 'rgba(255, 106, 61, 0.35)',
                color: '#FF6A3D',
                backgroundColor: 'rgba(255,255,255,0.55)',
                '&:hover': {
                  borderColor: '#FF6A3D',
                  backgroundColor: 'rgba(255, 106, 61, 0.08)',
                },
              }}
            >
              {rejectLoading ? <><CircularProgress size={20} /> Rejecting...</> : 'Reject'}
            </Button>
          </form>

          {isGmailInvite && (
            <>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">or</Typography>
              </Divider>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <GoogleInviteRegisterButton
                  selectedRole={formData.role}
                  inviteToken={inviteToken}
                  onError={onError}
                  studentSelfConsent={studentSelfConsent}
                  agreeToTerms={agreeToTerms}
                  legalDocumentAvailable={legalDocumentAvailable}
                />
              </Box>
            </>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account? <Link to="/login" style={{ fontWeight: 600, color: '#FF6A3D' }}>Sign in</Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InviteRegister;
